import { NgRedux } from '@angular-redux/store';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { PageScrollInstance, PageScrollService } from 'ngx-page-scroll';
import { Unsubscribe } from 'redux';
import { Observable } from 'rxjs/Observable';
import { APP_CONFIG } from '../../../api/config';
import { ManageStayDelegate } from '../../../api/delegates';
import { IMessage } from '../../../api/interfaces/Messages/IMessage';
import { Hotel, MessageType, PastStay, User } from '../../../api/model';
import { HasherService, HotelService, LogService, StayService } from '../../../api/services';
import { DateUtils } from '../../../common';
import { VALID_NAME_FORM_REGEX } from '../../../common/form-validator.util';
import { MessagesUtil } from '../../../common/messages.util';
import { IAppConfig, IAppState } from '../../../store';
import { BRAND_PATH, WriteReview } from './write-review-model';

const equals = (one: NgbDateStruct, two: NgbDateStruct) =>
  one && two && two.year === one.year && two.month === one.month && two.day === one.day;

const before = (one: NgbDateStruct, two: NgbDateStruct) =>
  !one || !two ? false : one.year === two.year ? one.month === two.month ? one.day === two.day
    ? false : one.day < two.day : one.month < two.month : one.year < two.year;

const after = (one: NgbDateStruct, two: NgbDateStruct) =>
  !one || !two ? false : one.year === two.year ? one.month === two.month ? one.day === two.day
    ? false : one.day > two.day : one.month > two.month : one.year > two.year;

const TOP_ID = '#writeAReviewTop';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'write-review',
  templateUrl: './write-review.component.html',
  styleUrls: ['./write-review.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WriteReviewComponent implements OnInit, OnDestroy {

  public CLASS_NAME = 'WriteReviewComponent';

  @ViewChild('bvform') bvForm;

  private state: IAppState;
  private unsubscribe: Unsubscribe;

  public member: User;
  public hotel: Observable<Hotel>;
  public form: FormGroup;

  public hotelChainCode = '';
  public hotelMnemonic = '';
  public iKnowConfirmationNumberLabel = '';
  public serviceLoading = true;
  public submitted = false;
  public disabledSubmit = true;
  public iKnowConfirmationNumber = true;
  public showMessage = false;

  public message: IMessage;
  public translations: { [key: string]: string };
  public paragraphSingIn1: string;
  public paragraphSingIn2: string;
  public paragraphSingIn: string;

  public model: NgbDateStruct;
  public firstDayOfWeek: number;
  public showCheckOutCalendar = false;
  public actualDate: Date;
  public _endDate: Date;
  public minMappedDate: NgbDateStruct;
  public maxMappedDate: NgbDateStruct;
  public _endDateStruct: NgbDateStruct;
  public hoveredDate: NgbDateStruct;
  public startCalendarDate: any;

  public bvReview = new WriteReview();

  constructor(@Inject(APP_CONFIG) protected appConfig: IAppConfig,
              private activatedRoute: ActivatedRoute,
              private ngRedux: NgRedux<IAppState>,
              private hotelService: HotelService,
              private hasherService: HasherService,
              private stayService: StayService,
              private logService: LogService,
              private pageScrollService: PageScrollService,
              private manageStayDelegate: ManageStayDelegate,
              @Inject(DOCUMENT) private document) {
    this.bvReview.targetParams = {...this.appConfig.bvReviews.params};
    this.activatedRoute.params.subscribe(params => {
      this.hotelMnemonic = params['hotelCode'];
      this.actualDate = new Date();
    });
  }

  ngOnInit() {
    this.firstDayOfWeek = 7;
    this.hotel = this.hotelService.getHotel(this.hotelMnemonic);
    this.hotel.subscribe(hotel => {
      this.hotelChainCode = hotel.chainCode ? hotel.chainCode : '';
    }, error => {
      this.logService.error(`Error retrieving hotel: ${this.hotelMnemonic}`, this.CLASS_NAME, error);
    });
    this.manageStayDelegate.fetchMember();
    this.updateFromState();
    this.unsubscribe = this.ngRedux.subscribe(() => {
      this.updateFromState();
    });
    this.defineReactiveForm();
  }

  public ngOnDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  public updateFromState() {
    this.state = this.ngRedux.getState();
    if (!this.state) {
      return;
    }

    if (!!this.state.member) {
      this.member = this.state.member;
      this.getMemberStays();
    }

    if (!!this.state.translations) {
      this.translations = MessagesUtil.filterTranslations(this.state.translations, 'hotelreview');
      this.iKnowConfirmationNumberLabel = !!this.translations ? this.translations['i.dont.know.confirmation'] : '';
      this.message = <IMessage>{key: 'page.level.error.message', messageType: MessageType.ERROR};

      if (!!this.translations['sign.in']) {
        let paragraphSingIn = this.translations['sign.in'].split('<span>');
        this.paragraphSingIn1 = paragraphSingIn[0];
        paragraphSingIn = paragraphSingIn[1].split('</span>');
        this.paragraphSingIn = paragraphSingIn[0];
        this.paragraphSingIn2 = paragraphSingIn[1];
      }
    }
  }

  toggleFields() {
    this.iKnowConfirmationNumber = !this.iKnowConfirmationNumber;
    this.iKnowConfirmationNumberLabel = !!this.translations && this.iKnowConfirmationNumber ? this.translations['i.dont.know.confirmation'] : this.translations['i.know.confirmation'];
    this.showCheckOutCalendar = false;
    this.submitted = false;
    this.form.clearValidators();
    this.defineReactiveForm();
  }

  onSubmit() {
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }

    let confirmationNumber = '', lastName = '', firstName = '', checkInDate = '';
    const checkOutDate = DateUtils.formatDate(this.form.controls.checkOutDate.value);
    if (this.iKnowConfirmationNumber) {
      confirmationNumber = this.form.controls.confirmationNumber.value;
      lastName = this.form.controls.lastName.value;
    } else {
      lastName = this.form.controls.lastNameKC.value;
      firstName = this.form.controls.firstName.value;
      checkInDate = DateUtils.formatDate(this.form.controls.checkInDate.value);
    }

    this.hasherService.get(confirmationNumber, firstName, lastName, checkInDate, checkOutDate, this.hotelMnemonic)
      .subscribe((hash: string) => {
          this.bvReview.hash = hash;
          this.bvReview.url = this.buildWriteReviewURL();
          this.bvReview.targetUrl = this.buildTargetURL(this.bvReview.hash);
          setTimeout(() => {
            this.bvForm.nativeElement.submit();
          }, 500);
        },
        error => {
          this.message = <IMessage>{...this.message, key: 'page.level.error.message', messageType: MessageType.ERROR};
          this.displayErrorMessage();
          this.logService.error(`Error getting hash ${confirmationNumber}`, this.CLASS_NAME, error);
        },
        () => {
          this.logService.info('Retrieved all hasher information', this.CLASS_NAME, '');
        });
  }

  private buildWriteReviewURL(): string {
    const bvurl = this.appConfig.bvReviews.urls.bv;
    const bvPath = this.appConfig.bvReviews.urls.bvPath.slice(0);
    return bvurl + bvPath.replace('{chaincode}', this.hotelChainCode)
      .replace('{language}', this.appConfig.locale.language)
      .replace('{country}', this.appConfig.locale.country)
      .replace('{mnemonic}', this.hotelMnemonic);
  }

  private buildTargetURL(hash) {
    const brandPath = BRAND_PATH[this.hotelChainCode.toLowerCase()] || '';
    const submit = this.appConfig.bvReviews.urls.submit.slice(0);
    return submit
      .replace('{brandpath}', brandPath || '')
      .replace('{country}', this.appConfig.locale.country)
      .replace('{language}', this.appConfig.locale.language)
      .replace('{mnemonic}', this.hotelMnemonic)
      .replace('{hash}', hash);
  }

  private defineReactiveForm() {
    if (!this.iKnowConfirmationNumber) {
      this.form = new FormGroup({
        firstName: new FormControl('', [Validators.required, Validators.pattern(VALID_NAME_FORM_REGEX)]),
        lastNameKC: new FormControl('', [Validators.required, Validators.pattern(VALID_NAME_FORM_REGEX)])
      });
    } else {
      this.form = new FormGroup({
        confirmationNumber: new FormControl('', [Validators.required, Validators.min(10000000)]),
        lastName: new FormControl('', [Validators.required, Validators.pattern(VALID_NAME_FORM_REGEX)]),
        checkOutDate: new FormControl('', [Validators.required])
      });
    }
  }

  get endDate(): Date {
    return this._endDate;
  }

  @Input()
  set endDate(value: Date) {
    if (!value) {
      return;
    }
    this._endDate = value;
    this._endDateStruct = this.toStruct(this._endDate);
  }

  onDateChange(date: NgbDateStruct) {
    this.endDate = this.fromStruct(date);
    this.form.patchValue({checkOutDate: this.endDate});
    this.showCheckOutCalendar = false;
  }

  openCalendar(e: Event) {
    this.minMappedDate = null;
    this.maxMappedDate = this.toStruct(this.actualDate);
    if (this._endDate) {
      this.startCalendarDate = {year: this._endDate.getUTCFullYear(), month: this._endDate.getUTCMonth()};
    } else {
      this.startCalendarDate = {year: this.actualDate.getUTCFullYear(), month: this.actualDate.getUTCMonth()};
    }
    this.showCheckOutCalendar = true;
  }

  closeCalendar(e: Event) {
    this.showCheckOutCalendar = false;
  }

  goToTop(): void {

    const pageScrollInstance: PageScrollInstance = PageScrollInstance.simpleInstance(this.document, TOP_ID);
    this.pageScrollService.start(pageScrollInstance);
  }

  getMemberStays() {
    if (this.member.isExplicit()) {
      this.stayService.getStaysInPeriod().subscribe((stays: PastStay[]) => {
        stays = stays.filter(stay => stay.hotelMnemonic === this.hotelMnemonic);
        if (stays.length === 0) {
          this.message = <IMessage>{...this.message, key: 'page.level.error.message', messageType: MessageType.ERROR};
          this.displayErrorMessage();
          this.serviceLoading = false;
        } else if (stays.length === 1) {
          this.showMessage = false;
          const stay = stays[0];
          this.hasherService.getHashByStayId(stay.stayId).subscribe((hash: string) => {
            this.bvReview.hash = hash;
            this.bvReview.url = this.buildWriteReviewURL();
            this.bvReview.targetUrl = this.buildTargetURL(this.bvReview.hash);
            setTimeout(() => {
              this.bvForm.nativeElement.submit();
            }, 500);
          });
        } else if (stays.length > 1) {
          this.message = <IMessage>{...this.message, key: 'page.level.error.message.multiple', messageType: MessageType.ERROR};
          this.displayErrorMessage();
          this.serviceLoading = false;
        }
      });
    } else {
      this.serviceLoading = false;
    }
  }

  displayErrorMessage() {
    this.showMessage = true;
    this.goToTop();
  }

  isFrom = date => equals(date, this._endDateStruct);
  isTo = date => equals(date, this._endDateStruct);
  fromStruct = (struct: NgbDateStruct) => new Date(struct.year, struct.month - 1, struct.day);
  toStruct = (date: Date) => <NgbDateStruct>{day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear()};

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/routes/write-review/write-review/write-review.component.ts